import type { StateCreator } from 'zustand';
import type { DocumentationStoreSlices } from '../types';
import type { OptimisticUpdatesSlice, OptimisticUpdatesSliceState } from './types';
import { DocumentationViewItem, isDirectoryItemType } from 'store/documentation/model/types';
import { mapVisibleRows } from '../filters/model';

export * from './types';

const INITIAL_OPTIMISTIC_UPDATES_STATE: OptimisticUpdatesSliceState = {
  pendingRowIds: new Set<string>(),
  removedRowIds: new Set<string>(),
  rollbackCache: new Map<string, DocumentationViewItem>(),
};

type OptimisticUpdatesSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  OptimisticUpdatesSlice
>;

export const createOptimisticUpdatesSlice: OptimisticUpdatesSliceCreator = (set, get) => ({
  ...INITIAL_OPTIMISTIC_UPDATES_STATE,

  markRowsAsPending: (ids: string[]): void => {
    if (ids.length === 0) return;

    const { pendingRowIds } = get();
    const newPendingIds = new Set(pendingRowIds);
    ids.forEach((id) => newPendingIds.add(id));
    set({ pendingRowIds: newPendingIds });
  },

  removeRowsOptimistically: (ids, items): void => {
    if (ids.length === 0) return;

    const { removedRowIds, rollbackCache, pendingRowIds } = get();
    const newRemovedIds = new Set(removedRowIds);
    const newRollbackCache = new Map(rollbackCache);
    const newPendingIds = new Set(pendingRowIds);

    ids.forEach((id, index) => {
      newRemovedIds.add(id);
      newPendingIds.delete(id);

      const item = items[index];
      if (item) {
        newRollbackCache.set(id, item);
      }
    });

    set({
      removedRowIds: newRemovedIds,
      rollbackCache: newRollbackCache,
      pendingRowIds: newPendingIds,
    });
  },

  confirmRowsRemoval: (ids: string[]): void => {
    if (ids.length === 0) return;

    const { 
      removedRowIds, 
      rollbackCache,
      currentDirectoryDirectories,
      currentDirectoryDocumentations,
    } = get();
    
    const idsSet = new Set(ids);
    const newRemovedIds = new Set(removedRowIds);
    const newRollbackCache = new Map(rollbackCache);

    ids.forEach((id) => {
      newRemovedIds.delete(id);
      newRollbackCache.delete(id);
    });

    // Also remove from source data to prevent reappearing after applyFilters
    const newDirectories = currentDirectoryDirectories.filter(
      (dir) => !dir._id || !idsSet.has(dir._id)
    );
    const newDocumentations = currentDirectoryDocumentations.filter(
      (doc) => !doc._id || !idsSet.has(doc._id)
    );
    set({
      removedRowIds: newRemovedIds,
      rollbackCache: newRollbackCache,
      currentDirectoryDirectories: newDirectories,
      currentDirectoryDocumentations: newDocumentations,
      ...mapVisibleRows(get(), (rows) =>
        rows.filter((row) => !row._id || !idsSet.has(row._id))
      ),
    });
  },

  rollbackRemovedRows: (ids: string[]): DocumentationViewItem[] => {
    const { rollbackCache, removedRowIds, pendingRowIds } = get();
    const restoredItems: DocumentationViewItem[] = [];

    ids.forEach((id) => {
      const item = rollbackCache.get(id);
      if (item) {
        restoredItems.push(item);
      }
    });

    const newRemovedIds = new Set(removedRowIds);
    const newRollbackCache = new Map(rollbackCache);
    const newPendingIds = new Set(pendingRowIds);

    ids.forEach((id) => {
      newRemovedIds.delete(id);
      newRollbackCache.delete(id);
      newPendingIds.delete(id);
    });

    set({
      removedRowIds: newRemovedIds,
      rollbackCache: newRollbackCache,
      pendingRowIds: newPendingIds,
    });

    return restoredItems;
  },

  clearPendingRows: (ids: string[]): void => {
    if (ids.length === 0) return;

    const { pendingRowIds } = get();
    const newPendingIds = new Set(pendingRowIds);
    ids.forEach((id) => newPendingIds.delete(id));
    set({ pendingRowIds: newPendingIds });
  },

  isRowPending: (id: string): boolean => {
    return get().pendingRowIds.has(id);
  },

  isRowRemoved: (id: string): boolean => {
    return get().removedRowIds.has(id);
  },

  resetOptimisticState: (): void => {
    set({
      pendingRowIds: new Set<string>(),
      removedRowIds: new Set<string>(),
      rollbackCache: new Map<string, DocumentationViewItem>(),
    });
  },

  markDirectoryAsNonEmpty: (directoryId: string): void => {
    if (!directoryId) return;

    const isTarget = (row: DocumentationViewItem): boolean =>
      row._id === directoryId && isDirectoryItemType(row.type);

    // Only update if the directory is listed.
    if (!get().filteredRows.some(isTarget)) {
      return;
    }

    set(
      mapVisibleRows(get(), (rows) =>
        rows.map((row) => (isTarget(row) ? { ...row, isEmptyDirectory: false } : row))
      )
    );
  },
});
